import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  Box,
  FilterOptionsState,
  FormHelperText,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { GenericObject } from 'types';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';

type HookFormAutoCompletePropsDisableRequired<
  T extends GenericObject | string
> = {
  disableRequiredProps: true;
  options?: T[] | never;
  getOptionLabel?: (option: T) => string | never;
  handleChange?: (option: T) => void | never;
  initialValueMatcher?: (value: string) => string | unknown | null | never;
  valueFromOption?: (option: T) => string;
  disabled?: boolean | never;
  mb?: number;
  fullWidth?: boolean;
  multiple?: boolean;
  groupBy?: (option: T) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
  sx?: SxProps<Theme>;
  slotProps?: AutocompleteProps<T, boolean, boolean, false>['slotProps'];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: string;
    },
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  disableClearable?: boolean;
};

export type BaseHookFormAutocompleteProps<T extends GenericObject | string> = {
  options: T[];
  getOptionLabel: (option: T) => string;
  handleChange?: (option: T) => void;
  initialValueMatcher?: (value: string) => string | unknown | null;
  valueFromOption?: (option: T) => string;
  disabled?: boolean;
  mb?: number;
  fullWidth?: boolean;
  multiple?: boolean;
  groupBy?: (option: T) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
  sx?: SxProps<Theme>;
  slotProps?: AutocompleteProps<T, boolean, boolean, false>['slotProps'];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: string;
    },
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  disableClearable?: boolean;
};

type HookFormAutocompleteProps<T extends GenericObject | string> =
  HookFormComponentProps &
    (
      | HookFormAutoCompletePropsDisableRequired<T>
      | BaseHookFormAutocompleteProps<T>
    );

export const HookFormAutocomplete = <T extends GenericObject | string>({
  name = '',
  control,
  options = [],
  label,
  errors,
  getOptionLabel,
  disabled,
  handleChange,
  initialValueMatcher,
  valueFromOption,
  mb = 2,
  fullWidth,
  multiple,
  shouldUnregister = false,
  sx,
  groupBy,
  renderGroup,
  renderOption,
  filterOptions,
  disableClearable,
  slotProps,
}: HookFormAutocompleteProps<T>) => (
  <Controller
    name={name}
    control={control}
    shouldUnregister={shouldUnregister}
    render={({ field: { onChange, value, onBlur, ref } }) => (
      <Autocomplete
        fullWidth={fullWidth}
        multiple={multiple}
        options={options}
        onChange={(__, data) =>
          handleChange
            ? handleChange(data as T)
            : onChange(valueFromOption ? valueFromOption(data as T) : data)
        }
        value={
          initialValueMatcher
            ? initialValueMatcher(value)
            : typeof value === 'string'
            ? options.find((o) => o === value)
            : value || (multiple ? [] : null)
        }
        onBlur={onBlur}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={(params) => (
          <Box sx={{ mb }}>
            <TextField
              {...params}
              label={label}
              fullWidth
              inputRef={ref}
              error={!!errors?.[name]?.message}
            />
            {errors?.[name]?.message && (
              <FormHelperText error id={`${name}Error`}>
                {errors?.[name]?.message}
              </FormHelperText>
            )}
          </Box>
        )}
        groupBy={groupBy}
        renderGroup={renderGroup}
        disabled={disabled}
        slotProps={slotProps}
        filterOptions={filterOptions}
        disableClearable={disableClearable}
        sx={sx}
      />
    )}
  />
);
